import type { Row } from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";

import { useThrottle } from "@/hooks/useThrottle";

import { hasClickableAncestor } from "./clickableElement";

/**
 * テーブルのユーザーインタラクション（スクロール、クリック)管理フック
 * - スクロール状態の検知とスロットリング
 * - 行クリックイベントの処理と競合回避
 * - キーボードアクセシビリティ対応
 */
interface UseTableInteractionOptions<TData> {
  /** 行クリック時のコールバック関数 */
  onRowClick: ((row: Row<TData>) => void) | null;
}

interface UseTableInteractionReturn<TData> {
  /** テーブルコンテナの参照 */
  containerRef: React.RefObject<HTMLDivElement>;
  /** スクロール状態（カラム固定時の影表示用） */
  isScrolling: boolean;
  /** 行クリックイベントハンドラ */
  handleRowClick: (
    event: React.MouseEvent<HTMLTableRowElement>,
    rowData: Row<TData>,
  ) => void;
}

export const useTableInteraction = <TData>({
  onRowClick,
}: UseTableInteractionOptions<TData>): UseTableInteractionReturn<TData> => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  // スクロール状態の検知（カラム固定時の影表示用）
  const handleThrottledScroll = useThrottle(
    useCallback(() => {
      setIsScrolling(!!containerRef.current?.scrollLeft);
    }, []),
    100,
  );

  // スクロールイベントリスナーの設定・クリーンアップ
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleThrottledScroll);
    return () => container.removeEventListener("scroll", handleThrottledScroll);
  }, [handleThrottledScroll]);

  // 行クリックイベントの処理
  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, rowData: Row<TData>) => {
      if (onRowClick === null || !(event.target instanceof HTMLElement)) {
        return;
      }

      // セル内のクリッカブル要素がクリックされた場合は行クリックを無効化
      if (hasClickableAncestor(event.target, event.currentTarget)) {
        event.stopPropagation();
        return;
      }

      onRowClick(rowData);
    },
    [onRowClick],
  );

  return {
    containerRef,
    isScrolling,
    handleRowClick,
  };
};
